/**
 * Display preferences — personal to this device, like the card layout (layout/model.ts):
 * stored in `localStorage`, no backend round trip. Needs no props at all, so it is
 * trivially portable to any page.
 */

import { useState } from "react";
import { SETTINGS_MAX, SETTINGS_MIN, loadSettings, saveSetting, type AppSettings } from "../settings";

/**
 * A labelled +/− stepper, the same visual language as the layout's column picker.
 *
 * `toggle` puts an on/off checkbox at the start of the label, on the same line, for a setting
 * that can be switched off entirely. A checkbox rather than a 0 that means "off": the stepper
 * is a pair of buttons around a count, so there is nowhere to write "Off" in it, and a 0 that
 * secretly means something else loses the number you had — switching back on should return to
 * the duration you chose, not to zero. Unticked, the stepper dims and stops responding.
 */
function SettingStepper({
  label,
  value,
  onChange,
  min = SETTINGS_MIN,
  max = SETTINGS_MAX,
  noun = "lines",
  toggle,
}: {
  label: string;
  value: number;
  onChange: (next: number) => void;
  min?: number;
  max?: number;
  /** What the count counts, for the buttons' accessible names ("Fewer lines"). */
  noun?: string;
  toggle?: { checked: boolean; onChange: (next: boolean) => void };
}) {
  const off = toggle !== undefined && !toggle.checked;
  // `display: contents` (same trick `.task-actions` uses): this row's own box disappears,
  // so its four children become direct items of the parent `.setting-grid` and line up in
  // its columns — the label, the −, the count and the + each stay aligned across rows.
  return (
    <div className={off ? "setting-row is-off" : "setting-row"} role="group" aria-label={label}>
      {toggle ? (
        <label className="small muted setting-toggle-label">
          <input
            type="checkbox"
            checked={toggle.checked}
            onChange={(e) => toggle.onChange(e.target.checked)}
          />
          {label}
        </label>
      ) : (
        <span className="small muted">{label}</span>
      )}
      <button
        type="button"
        className="btn ghost small"
        onClick={() => onChange(Math.max(value - 1, min))}
        disabled={off || value <= min}
        aria-label={`Fewer ${noun}`}
        title="Fewer"
      >
        −
      </button>
      <span className="count" aria-live="polite">
        {value}
      </span>
      <button
        type="button"
        className="btn ghost small"
        onClick={() => onChange(Math.min(value + 1, max))}
        disabled={off || value >= max}
        aria-label={`More ${noun}`}
        title="More"
      >
        +
      </button>
    </div>
  );
}

/** A label + checkbox row for a boolean display setting, e.g. one of Colorize Tasks'
 * three switches — the whole row is the label, same as `.cards-picker-row` elsewhere. */
function SettingCheckbox({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (next: boolean) => void;
}) {
  return (
    <label className="setting-checkbox-row">
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} />
      {label}
    </label>
  );
}

export function DisplayCard() {
  const [settings, setSettings] = useState<AppSettings>(() => loadSettings());

  function update<K extends keyof AppSettings>(key: K, value: AppSettings[K]) {
    setSettings((current) => ({ ...current, [key]: value }));
    saveSetting(key, value);
  }

  return (
    <section className="panel">
      <header>
        <h2>Display</h2>
        <span className="muted small">Personal to this device</span>
      </header>

      <div className="setting-grid">
        <SettingStepper
          label="Notes shown on a task"
          value={settings.taskNotesLines}
          onChange={(v) => update("taskNotesLines", v)}
        />
        <SettingStepper
          label="Notes card preview lines"
          value={settings.noteBodyLines}
          onChange={(v) => update("noteBodyLines", v)}
        />
      </div>
      <p className="muted small" style={{ margin: "0.6rem 0 0" }}>
        0 hides the preview entirely. Either way, the full text is never lost - this only
        changes how much shows before the eye icon offers to expand it.
      </p>

      <p className="small muted" style={{ margin: "1rem 0 0.4rem" }}>
        Auto-hide - after a result shows
      </p>
      <div className="setting-grid">
        <SettingStepper
          label="Hide a finished ✓ by itself after (seconds)"
          value={settings.inboxAiAutoClearSeconds}
          onChange={(v) => update("inboxAiAutoClearSeconds", v)}
          min={1}
          noun="seconds"
          toggle={{
            checked: settings.inboxAiAutoClear,
            onChange: (v) => update("inboxAiAutoClear", v),
          }}
        />
      </div>
      <p className="muted small" style={{ margin: "0.6rem 0 0" }}>
        Anywhere the app shows you how something went - the AI's answer on an Inbox item, an
        item the queue has just filed - it waits, then tidies itself away. Unticked, it waits for
        you to click the ✓. Anything that didn't succeed always waits, however long this is.
      </p>

      <p className="small muted" style={{ margin: "1rem 0 0.4rem" }}>
        Colorize Tasks - a muted glow on each row, purely decorative
      </p>
      <div style={{ display: "grid", gap: "0.3rem" }}>
        <SettingCheckbox
          label="Energy"
          checked={settings.colorizeEnergy}
          onChange={(v) => update("colorizeEnergy", v)}
        />
        <SettingCheckbox
          label="Overdue"
          checked={settings.colorizeOverdue}
          onChange={(v) => update("colorizeOverdue", v)}
        />
        <SettingCheckbox
          label="Important"
          checked={settings.colorizeImportant}
          onChange={(v) => update("colorizeImportant", v)}
        />
      </div>
    </section>
  );
}
